// src/pages/admin/ClassManager.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import {
  ArrowLeft,
  Plus,
  Edit2,
  UserPlus,
  UserMinus,
  ArrowRightLeft,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  X,
  Users,
  Clock,
  Search,
  RefreshCw,
  BookOpen,
  Trash2,
  Copy,
  Check
} from 'lucide-react';
import { getChildren, calculateAge } from '../../utils/childrenHelper';
import {
  getBrowserTimezone,
  timeToUTC,
  timeFromUTC,
  formatTimeDisplay,
  COMMON_TIMEZONES,
  TOOLTIP_ZONES,
} from '../../utils/timezoneUtils';
import { COURSE_PACKAGES } from '../../config/coursePackages';

// ─── CopyableId ───────────────────────────────────────────────────────────────

function CopyableId({ label, value }) {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1">
      <span className="text-xs text-gray-400">{label}:</span>
      <span className="text-xs font-mono text-gray-500" title={value}>
        {value.length > 22 ? value.slice(0, 22) + '…' : value}
      </span>
      <button
        onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="text-gray-300 hover:text-gray-500 transition-colors"
        title="Copy to clipboard"
      >
        {copied ? <Check size={11} className="text-green-500" /> : <Copy size={11} />}
      </button>
    </span>
  );
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = [
  { value: 'sunday',    label: 'Sunday' },
  { value: 'monday',   label: 'Monday' },
  { value: 'tuesday',  label: 'Tuesday' },
  { value: 'wednesday',label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday',   label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

const DIALECTS = [
  { value: 'egyptian',  label: 'Egyptian' },
  { value: 'lebanese',  label: 'Lebanese' },
  { value: 'syrian',  label: 'Syrian' },
  { value: 'palestinian',  label: 'Palestinian' },
  { value: 'moroccan',  label: 'Moroccan' },
  { value: 'khaleeji',  label: 'Khaleeji' },
  { value: 'fusha',     label: 'Fusha (MSA)' },
  { value: 'other',     label: 'Other' },
];

const CLASS_TYPES = [
  { value: 'group',   label: 'Group' },
  { value: 'private', label: 'Private' },
];

const AGE_GROUPS = [
  { value: 'young',  label: 'Young (5–8)' },
  { value: 'middle', label: 'Middle (9–12)' },
  { value: 'teen',   label: 'Teen (13–15)' },
  { value: 'adult',  label: 'Adults (18+)' },
];

const LEVEL_GROUPS = [
  { value: 'beginner',     label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced',     label: 'Advanced' },
];

const STATUS_OPTIONS = [
  { value: 'active',    label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
];

const EMPTY_CLASS_FORM = {
  title: '',
  teacherId: '',
  sessionsPerWeek: 1,
  slots: [{ dayOfWeek: 'monday', time: '16:00' }],
  duration: 60,
  type: 'group',
  dialect: 'egyptian',
  ageGroup: 'young',
  levelGroup: 'beginner',
  notes: '',
  status: 'active',
  compatiblePackages: [],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const labelOf = (arr, val) => arr.find(x => x.value === val)?.label ?? val;

// ─── Slot helpers ─────────────────────────────────────────────────────────────

// Backward compat: old docs have flat dayOfWeek/time; new docs have slots[].
// Always use this to get the slots array regardless of doc age.
function getClassSlots(cls) {
  if (cls.slots && cls.slots.length > 0) return cls.slots;
  if (cls.dayOfWeek && cls.time) return [{ dayOfWeek: cls.dayOfWeek, time: cls.time }];
  return [];
}

// "Mon 4:00 PM · Thu 4:00 PM"
function formatSchedule(cls) {
  return getClassSlots(cls)
    .map(s => {
      const day = DAYS_OF_WEEK.find(d => d.value === s.dayOfWeek)?.label ?? s.dayOfWeek;
      return day.slice(0, 3) + ' ' + formatTime(s.time);
    })
    .join(' · ');
}

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m} ${ampm}`;
}

// ─── Timezone Tooltip ─────────────────────────────────────────────────────────
// All slot times are stored as UTC HH:MM strings. This tooltip lets staff
// in any timezone see the equivalent local time without manual math.

// TOOLTIP_ZONES imported from timezoneUtils

// All slot times are stored as UTC HH:MM strings. Tooltip shows conversions from UTC.
function getTimezoneConversions(utcTimeStr, utcDayName) {
  if (!utcTimeStr || !utcDayName) return [];
  try {
    return TOOLTIP_ZONES.map(({ label, tz }) => {
      const { dayChanged } = timeFromUTC(utcTimeStr, utcDayName, tz);
      return { label, time: formatTimeDisplay(utcTimeStr, tz), nextDay: dayChanged };
    });
  } catch {
    return [];
  }
}

function TimezoneTooltip({ timeStr, dayOfWeek, children }) {
  const conversions = getTimezoneConversions(timeStr, dayOfWeek);
  const [visible, setVisible] = useState(false);
  const [coords,  setCoords]  = useState({ top: 0, left: 0 });
  const triggerRef            = useRef(null);

  // Detect touch-only devices once on mount — never changes during the session.
  const [isTouch] = useState(() => window.matchMedia('(hover: none)').matches);

  const show = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top:  rect.top + window.scrollY - 8,
      left: rect.left + window.scrollX + rect.width / 2,
    });
    setVisible(true);
  }, []);

  const hide = useCallback(() => setVisible(false), []);

  const toggle = useCallback(() => {
    if (visible) hide(); else show();
  }, [visible, show, hide]);

  if (!conversions.length) return <>{children}</>;

  const tooltip = visible && ReactDOM.createPortal(
    <>
      {/* Touch-only: full-screen overlay closes the tooltip when tapped */}
      {isTouch && (
        <div
          className="fixed inset-0 z-[9998] bg-black bg-opacity-20"
          style={{ pointerEvents: 'auto' }}
          onClick={hide}
        />
      )}
      <div
        className="fixed z-[9999]"
        style={{
          top:          coords.top,
          left:         coords.left,
          transform:    'translate(-50%, -100%)',
          pointerEvents: isTouch ? 'auto' : 'none',
        }}
      >
        <div
          className="bg-gray-900 text-white text-xs rounded-lg shadow-2xl p-3 relative"
          style={{ minWidth: 200 }}
        >
          {/* Touch-only: × close button inside the panel */}
          {isTouch && (
            <button
              onClick={hide}
              className="absolute top-1.5 right-1.5 text-gray-400 hover:text-white p-0.5"
            >
              <X size={12} />
            </button>
          )}
          <span className="block text-gray-400 uppercase tracking-wider mb-2 font-semibold" style={{ fontSize: 10 }}>
            🌍 Time zone reference
          </span>
          {conversions.map(({ label, time, nextDay }) => (
            <span key={label} className="flex justify-between gap-4 leading-6">
              <span className="text-gray-400">{label}</span>
              <span className="font-semibold text-white whitespace-nowrap">
                {time}
                {nextDay && <span className="ml-1 text-yellow-400" style={{ fontSize: 10 }}>+1d</span>}
              </span>
            </span>
          ))}
          {/* Arrow pointing down */}
          <div
            className="absolute left-1/2 bg-gray-900 rotate-45"
            style={{ top: '100%', marginTop: -4, marginLeft: -4, width: 8, height: 8 }}
          />
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <>
      <span
        ref={triggerRef}
        {...(isTouch
          ? { onClick: toggle }
          : { onMouseEnter: show, onMouseLeave: hide }
        )}
        className="inline-flex items-center"
      >
        {children}
      </span>
      {tooltip}
    </>
  );
}

// Build a denormalized enrolledStudent entry from live user + child data
function buildEnrolledStudent(user, child, overrides = {}) {
  return {
    userId: user.uid,
    childId: child.id,
    childName: child.name ?? '',
    parentEmail: user.email ?? '',
    parentFirstName: user.firstName ?? '',
    parentLastName: user.lastName ?? '',
    enrollmentType: 'course', // trials are managed separately via TrialManager
    sessionsCompleted: 0,
    sessionsTotal: 6,
    enrolledAt: new Date().toISOString(),
    ...overrides,
  };
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const map = {
    active:    'bg-green-100 text-green-700',
    suspended: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {labelOf(STATUS_OPTIONS, status)}
    </span>
  );
}

function TypeBadge({ type }) {
  const map = {
    group:   'bg-blue-100 text-blue-700',
    private: 'bg-purple-100 text-purple-700',
    trial:   'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${map[type] ?? 'bg-gray-100 text-gray-500'}`}>
      {labelOf(CLASS_TYPES, type)}
    </span>
  );
}

// Orange badge shown only on intensive (2+/week) classes
function IntensityBadge({ sessionsPerWeek }) {
  if (!sessionsPerWeek || sessionsPerWeek <= 1) return null;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
      {sessionsPerWeek}×/week
    </span>
  );
}

// ─── Class Form Modal ──────────────────────────────────────────────────────────

function ClassFormModal({ cls, teachers, classes, onSave, onClose, saving }) {
  const isEditing = !!cls?.id;
  const browserTz = getBrowserTimezone();

  // When editing, convert stored UTC slot times back to the browser's local timezone.
  function toLocalSlots(slots, tz) {
    return (slots ?? []).map(slot => {
      const { localTime: time, localDay: dayOfWeek } = timeFromUTC(slot.time, slot.dayOfWeek, tz);
      return { dayOfWeek, time };
    });
  }

  // Normalize: always work with slots[] internally. When editing, reconstruct from
  // whatever format the doc is in (old flat fields or new slots array).
  const initialForm = cls
    ? {
        ...EMPTY_CLASS_FORM,
        ...cls,
        slots: toLocalSlots(getClassSlots(cls), browserTz),
        sessionsPerWeek: cls.sessionsPerWeek ?? getClassSlots(cls).length,
      }
    : { ...EMPTY_CLASS_FORM };

  const [form, setForm]               = useState(initialForm);
  const [errors, setErrors]           = useState({});
  // Per-slot availability confirmations, keyed by slot index
  const [confirmedOutside, setConfirmedOutside] = useState({});
  const [makeupWarnings, setMakeupWarnings]     = useState([]);
  const [confirmedMakeup, setConfirmedMakeup]   = useState(false);
  const [checkingMakeups, setCheckingMakeups]   = useState(false);
  const [entryTimezone, setEntryTimezone]       = useState(browserTz);

  // When admin changes the entry timezone, round-trip all slot times through UTC.
  function handleTimezoneChange(newTz) {
    setForm(f => ({
      ...f,
      slots: (f.slots ?? []).map(slot => {
        const { utcTime, utcDay } = timeToUTC(slot.time, slot.dayOfWeek, entryTimezone);
        const { localTime: time, localDay: dayOfWeek } = timeFromUTC(utcTime, utcDay, newTz);
        return { dayOfWeek, time };
      }),
    }));
    setEntryTimezone(newTz);
  }

  const selectedTeacher = teachers.find(t => t.id === form.teacherId);

  // ── Non-slot field setter ────────────────────────────────────────────────────
  function setField(field, value) {
    if (field === 'compatiblePackages' && value.length > 0) {
      const pkgTypes = [...new Set(value.map(id => COURSE_PACKAGES[id]?.type).filter(Boolean))];
      if (pkgTypes.length === 1) {
        setForm(f => ({ ...f, [field]: value, type: pkgTypes[0] }));
        setErrors(e => { const n = { ...e }; delete n[field]; return n; });
        return;
      }
    }
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => { const n = { ...e }; delete n[field]; return n; });
    // Changing teacher or duration invalidates all slot confirmations
    if (['teacherId', 'duration'].includes(field)) {
      setConfirmedOutside({});
      setMakeupWarnings([]);
      setConfirmedMakeup(false);
    }
  }

  // ── Slot field setter ────────────────────────────────────────────────────────
  function updateSlot(index, field, value) {
    setForm(f => ({
      ...f,
      slots: f.slots.map((s, i) => i === index ? { ...s, [field]: value } : s),
    }));
    // Invalidate this slot's confirmation when its day or time changes
    setConfirmedOutside(prev => { const n = { ...prev }; delete n[index]; return n; });
    setErrors(e => { const n = { ...e }; delete n['slot_' + index]; delete n['availabilityConfirm_' + index]; return n; });
    setMakeupWarnings([]);
    setConfirmedMakeup(false);
  }

  // ── Sessions-per-week stepper ────────────────────────────────────────────────
  function handleSessionsPerWeekChange(newCount) {
    const count = Math.max(1, Math.min(7, newCount));
    setForm(f => {
      const current = f.slots ?? [];
      if (count === current.length) return { ...f, sessionsPerWeek: count };

      if (count < current.length) {
        // Shrink — drop trailing slots
        return { ...f, sessionsPerWeek: count, slots: current.slice(0, count) };
      }

      // Grow — add slots using the next unused weekday in order
      const usedDays = new Set(current.map(s => s.dayOfWeek));
      const added = [...current];
      for (const d of DAYS_OF_WEEK) {
        if (added.length >= count) break;
        if (!usedDays.has(d.value)) {
          added.push({ dayOfWeek: d.value, time: current[0]?.time ?? '16:00' });
          usedDays.add(d.value);
        }
      }
      return { ...f, sessionsPerWeek: count, slots: added };
    });
    setConfirmedOutside({});
    setMakeupWarnings([]);
    setConfirmedMakeup(false);
  }

  // ── Per-slot availability check ──────────────────────────────────────────────
  // Returns null (teacher not selected or duration not set),
  // or { state: 'no_slots' | 'fits' | 'conflict', slotDesc? }
  // Slot times are in entryTimezone; teacher availability is stored in UTC.
  function checkSlotAvailability(slot) {
    if (!selectedTeacher || !slot?.dayOfWeek || !slot?.time || !form.duration) return null;

    // Convert slot to UTC for comparison
    const { utcTime: slotUTCTime, utcDay: slotUTCDay } = timeToUTC(slot.time, slot.dayOfWeek, entryTimezone);

    const teacherSlots = (selectedTeacher.availability ?? []).filter(
      s => s.dayOfWeek === slotUTCDay
    );
    if (teacherSlots.length === 0) return { state: 'no_slots' };

    const toMins = t => {
      const [h, m] = t.split(':').map(Number);
      const mins = h * 60 + m;
      // "00:00" as an end time means midnight = end of day = 1440 min
      return mins === 0 ? 1440 : mins;
    };

    const classStart = toMins(slotUTCTime);
    const classEnd   = classStart + (form.duration ?? 60);

    const fits = teacherSlots.some(s => toMins(s.startTime) <= classStart && toMins(s.endTime) >= classEnd);
    if (fits) return { state: 'fits' };

    const slotDesc = teacherSlots.map(s => formatTime(s.startTime) + '–' + formatTime(s.endTime)).join(', ');
    return { state: 'conflict', slotDesc };
  }

  // ── Validate ─────────────────────────────────────────────────────────────────
  function validate() {
    const e = {};
    const toMins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

    if (!form.teacherId) e.teacherId = 'Please select a teacher';
    if (!form.duration || form.duration < 15) e.duration = 'Duration must be at least 15 min';

    const slots = form.slots ?? [];

    // Each slot must have day + time
    slots.forEach((slot, i) => {
      if (!slot.dayOfWeek || !slot.time) e['slot_' + i] = 'Day and time are required';
    });

    // No duplicate days
    const seen = new Set();
    slots.forEach((slot, i) => {
      if (seen.has(slot.dayOfWeek)) e['slot_' + i] = 'Each day can only appear once';
      seen.add(slot.dayOfWeek);
    });

    // Teacher double-booking: each slot vs every OTHER existing class for this teacher.
    // Convert new slot to UTC first since all stored class times are UTC.
    if (form.teacherId && form.duration >= 15) {
      slots.forEach((slot, i) => {
        if (!slot.dayOfWeek || !slot.time) return; // already flagged above
        const { utcTime: slotUTC, utcDay: slotUTCDay } = timeToUTC(slot.time, slot.dayOfWeek, entryTimezone);
        const newStart = toMins(slotUTC);
        const newEnd   = newStart + Number(form.duration);

        // Check against all slots of the existing class (stored in UTC)
        const conflicts = (classes ?? []).filter(existing => {
          if (existing.id === cls?.id) return false;
          if (existing.teacherId !== form.teacherId) return false;
          if (existing.status === 'suspended') return false;
          return getClassSlots(existing).some(exSlot => {
            if (exSlot.dayOfWeek !== slotUTCDay) return false;
            const exStart = toMins(exSlot.time);
            const exEnd   = exStart + Number(existing.duration ?? 60);
            return newStart < exEnd && exStart < newEnd;
          });
        });

        if (conflicts.length > 0 && !e['slot_' + i]) {
          const { utcDay: slotUTCDayErr } = timeToUTC(slot.time, slot.dayOfWeek, entryTimezone);
          const desc = conflicts.map(c => formatTime(getClassSlots(c).find(s => s.dayOfWeek === slotUTCDayErr)?.time ?? c.time) + ' (' + c.duration + ' min, ' + c.type + ')').join(', ');
          e['slot_' + i] = (selectedTeacher?.name ?? 'This teacher') + ' already has a class on ' +
            slotUTCDayErr.charAt(0).toUpperCase() + slotUTCDayErr.slice(1) + ' (UTC): ' + desc;
        }
      });
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // ── Submit ───────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    // Availability confirmation — check every slot
    const slots = form.slots ?? [];
    let needsAvailConfirm = false;
    const newErrors = {};
    slots.forEach((slot, i) => {
      const check = checkSlotAvailability(slot);
      const needsConfirm = check?.state === 'conflict' || check?.state === 'no_slots';
      if (needsConfirm && !confirmedOutside[i]) {
        newErrors['availabilityConfirm_' + i] = 'Please confirm you want to schedule outside availability.';
        needsAvailConfirm = true;
      }
    });
    if (needsAvailConfirm) { setErrors(prev => ({ ...prev, ...newErrors })); return; }

    // Makeup session conflict check — across all slots.
    // Convert slot times to UTC before comparing with scheduledAt (UTC ISO strings).
    if (!confirmedMakeup && form.teacherId && form.duration) {
      const hasSlotsToCheck = slots.some(s => s.dayOfWeek && s.time);
      if (hasSlotsToCheck) {
        setCheckingMakeups(true);
        try {
          const toMins = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
          const DAY_INDEX = { sunday:0, monday:1, tuesday:2, wednesday:3, thursday:4, friday:5, saturday:6 };

          const snap = await getDocs(
            query(
              collection(db, 'sessions'),
              where('teacherId',  '==', form.teacherId),
              where('scheduledAt', '>=', new Date().toISOString())
            )
          );

          const seenIds = new Set();
          const allConflicts = [];

          slots.forEach(slot => {
            if (!slot.dayOfWeek || !slot.time) return;
            // Convert slot's local time to UTC for comparison
            const { utcTime, utcDay } = timeToUTC(slot.time, slot.dayOfWeek, entryTimezone);
            const newStart  = toMins(utcTime);
            const newEnd    = newStart + Number(form.duration);
            const targetDay = DAY_INDEX[utcDay];

            snap.docs.forEach(docSnap => {
              const s = { id: docSnap.id, ...docSnap.data() };
              if (!s.isMakeup || seenIds.has(s.id) || !s.scheduledAt) return;
              const d = new Date(s.scheduledAt);
              if (d.getUTCDay() !== targetDay) return;
              const oStart = d.getUTCHours() * 60 + d.getUTCMinutes();
              const oEnd   = oStart + (s.duration ?? 60);
              if (newStart < oEnd && oStart < newEnd) {
                seenIds.add(s.id);
                allConflicts.push(s);
              }
            });
          });

          setCheckingMakeups(false);

          if (allConflicts.length > 0) {
            setMakeupWarnings(allConflicts);
            return;
          }
        } catch (err) {
          console.error('Makeup conflict check error:', err);
          setCheckingMakeups(false);
        }
      }
    }

    // Convert all slot times from entryTimezone → UTC before saving
    const utcSlots = (form.slots ?? []).map(slot => {
      const { utcTime: time, utcDay: dayOfWeek } = timeToUTC(slot.time, slot.dayOfWeek, entryTimezone);
      return { dayOfWeek, time };
    });

    onSave({ ...form, slots: utcSlots });
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  const slots = form.slots ?? [];
  const usedDays = new Set(slots.map(s => s.dayOfWeek));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEditing ? 'Edit Class' : 'Create Class'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* Title (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-xs font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={form.title ?? ''}
              onChange={e => setField('title', e.target.value)}
              placeholder="e.g. Monday Beginners · Fatima's Wednesday Group"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Teacher */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Teacher <span className="text-red-500">*</span>
            </label>
            <select
              value={form.teacherId}
              onChange={e => setField('teacherId', e.target.value)}
              className={'w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ' + (errors.teacherId ? 'border-red-400' : 'border-gray-300')}
            >
              <option value="">Select teacher…</option>
              {teachers.filter(t => t.active).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {errors.teacherId && <p className="text-xs text-red-500 mt-1">{errors.teacherId}</p>}
          </div>

          {/* Sessions per week stepper */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sessions per week</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleSessionsPerWeekChange((form.sessionsPerWeek ?? 1) - 1)}
                disabled={(form.sessionsPerWeek ?? 1) <= 1}
                className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center text-gray-700 font-bold text-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                −
              </button>
              <span className="w-6 text-center text-sm font-semibold text-gray-800">
                {form.sessionsPerWeek ?? 1}
              </span>
              <button
                type="button"
                onClick={() => handleSessionsPerWeekChange((form.sessionsPerWeek ?? 1) + 1)}
                disabled={(form.sessionsPerWeek ?? 1) >= 7}
                className="w-8 h-8 rounded-lg border border-gray-300 flex items-center justify-center text-gray-700 font-bold text-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                +
              </button>
              {(form.sessionsPerWeek ?? 1) > 1 && (
                <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">
                  Intensive · {form.sessionsPerWeek}×/week
                </span>
              )}
            </div>
          </div>

          {/* Dynamic slot rows — one per session */}
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <label className="block text-sm font-medium text-gray-700">
                Schedule <span className="text-red-500">*</span>
                {slots.length > 1 && (
                  <span className="ml-2 text-xs text-gray-400 font-normal">Duration applies to all sessions</span>
                )}
              </label>
              {/* Entry timezone dropdown — times are entered in this timezone, converted to UTC on save */}
              <select
                value={entryTimezone}
                onChange={e => handleTimezoneChange(e.target.value)}
                className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                title="Times are entered in this timezone and converted to UTC on save"
              >
                {COMMON_TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>

            {slots.map((slot, i) => {
              const availCheck = checkSlotAvailability(slot);
              return (
                <div key={i} className="space-y-2">
                  {slots.length > 1 && (
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Session {i + 1}</p>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {/* Day dropdown — disable already-used days */}
                    <select
                      value={slot.dayOfWeek}
                      onChange={e => updateSlot(i, 'dayOfWeek', e.target.value)}
                      className={'w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ' + (errors['slot_' + i] ? 'border-red-400' : 'border-gray-300')}
                    >
                      {DAYS_OF_WEEK.map(d => (
                        <option
                          key={d.value}
                          value={d.value}
                          disabled={d.value !== slot.dayOfWeek && usedDays.has(d.value)}
                        >
                          {d.label}{d.value !== slot.dayOfWeek && usedDays.has(d.value) ? ' (used)' : ''}
                        </option>
                      ))}
                    </select>

                    <input
                      type="time"
                      value={slot.time}
                      onChange={e => updateSlot(i, 'time', e.target.value)}
                      className={'w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ' + (errors['slot_' + i] ? 'border-red-400' : 'border-gray-300')}
                    />
                  </div>
                  {/* Live UTC preview */}
                  {slot.time && slot.dayOfWeek && (() => {
                    const { utcTime, utcDay } = timeToUTC(slot.time, slot.dayOfWeek, entryTimezone);
                    const dayShift = utcDay !== slot.dayOfWeek;
                    return (
                      <p className="text-xs text-gray-400 pl-1">
                        = {utcTime} UTC · {utcDay.charAt(0).toUpperCase() + utcDay.slice(1)}
                        {dayShift && <span className="ml-1.5 text-yellow-600 font-medium">⚠ day shifts in UTC</span>}
                      </p>
                    );
                  })()}

                  {errors['slot_' + i] && (
                    <p className="text-xs text-red-500">{errors['slot_' + i]}</p>
                  )}

                  {/* Per-slot availability status */}
                  {selectedTeacher && availCheck && (
                    availCheck.state === 'conflict' ? (
                      <div className="bg-orange-50 border border-orange-300 rounded-lg px-3 py-2.5 space-y-2">
                        <p className="text-xs font-semibold text-orange-800">
                          ⚠️ Outside {selectedTeacher.name}'s availability
                        </p>
                        <p className="text-xs text-orange-700">
                          Recorded {DAYS_OF_WEEK.find(d => d.value === slot.dayOfWeek)?.label} slots: <strong>{availCheck.slotDesc}</strong>
                        </p>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!confirmedOutside[i]}
                            onChange={e2 => {
                              setConfirmedOutside(prev => ({ ...prev, [i]: e2.target.checked }));
                              setErrors(prev => { const n = { ...prev }; delete n['availabilityConfirm_' + i]; return n; });
                            }}
                            className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-xs text-orange-800 font-medium">
                            Intentional — teacher agreed or availability not yet updated
                          </span>
                        </label>
                        {errors['availabilityConfirm_' + i] && (
                          <p className="text-xs text-red-600">{errors['availabilityConfirm_' + i]}</p>
                        )}
                      </div>
                    ) : availCheck.state === 'fits' ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-1.5 text-xs text-green-700">
                        ✓ Within {selectedTeacher.name}'s availability
                      </div>
                    ) : (
                      /* no_slots */
                      <div className="bg-orange-50 border border-orange-300 rounded-lg px-3 py-2.5 space-y-2">
                        <p className="text-xs font-semibold text-orange-800">
                          ⚠️ No availability recorded for {DAYS_OF_WEEK.find(d => d.value === slot.dayOfWeek)?.label}
                        </p>
                        <p className="text-xs text-orange-700">
                          Available days:{' '}
                          <strong>
                            {[...new Set((selectedTeacher.availability ?? []).map(s => s.dayOfWeek))]
                              .map(d => d.charAt(0).toUpperCase() + d.slice(1))
                              .join(', ') || 'none recorded'}
                          </strong>
                        </p>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!confirmedOutside[i]}
                            onChange={e2 => {
                              setConfirmedOutside(prev => ({ ...prev, [i]: e2.target.checked }));
                              setErrors(prev => { const n = { ...prev }; delete n['availabilityConfirm_' + i]; return n; });
                            }}
                            className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                          />
                          <span className="text-xs text-orange-800 font-medium">
                            Intentional — teacher agreed or availability not yet updated
                          </span>
                        </label>
                        {errors['availabilityConfirm_' + i] && (
                          <p className="text-xs text-red-600">{errors['availabilityConfirm_' + i]}</p>
                        )}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>

          {/* Duration — shared across all slots */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Duration (minutes)
            </label>
            <input
              type="number"
              min={15}
              max={180}
              value={form.duration}
              onChange={e => setField('duration', parseInt(e.target.value, 10))}
              className={'w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ' + (errors.duration ? 'border-red-400' : 'border-gray-300')}
            />
            {errors.duration && <p className="text-xs text-red-500 mt-1">{errors.duration}</p>}
          </div>

          {/* Type + Dialect */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={form.type} onChange={e => setField('type', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500">
                {CLASS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dialect</label>
              <select value={form.dialect} onChange={e => setField('dialect', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500">
                {DIALECTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Compatible Packages */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Compatible Packages</label>
            <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
              {Object.values(COURSE_PACKAGES).sort((a, b) => a.sortOrder - b.sortOrder).map(pkg => {
                const checked = (form.compatiblePackages ?? []).includes(pkg.id);
                return (
                  <label key={pkg.id} className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={e => {
                        const current = form.compatiblePackages ?? [];
                        const next = e.target.checked
                          ? [...current, pkg.id]
                          : current.filter(id => id !== pkg.id);
                        setField('compatiblePackages', next);
                      }}
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-gray-800">{pkg.shortLabel}</span>
                      <p className="text-xs text-gray-400">{pkg.subtitle} · ${pkg.price}</p>
                    </div>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              Students with matching paid packages enroll without warning. Leave empty to accept any package.
            </p>
          </div>

          {/* Age Group + Level */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age Group</label>
              <select value={form.ageGroup} onChange={e => setField('ageGroup', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500">
                {AGE_GROUPS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
              <select value={form.levelGroup} onChange={e => setField('levelGroup', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500">
                {LEVEL_GROUPS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>

          {/* Status (edit only) */}
          {isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={e => setField('status', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              rows={2}
              placeholder="e.g. Zoom link, special instructions…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            />
          </div>

          {/* Makeup session conflict warning */}
          {makeupWarnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle size={15} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-yellow-800">
                    Makeup session conflict{makeupWarnings.length > 1 ? 's' : ''} detected
                  </p>
                  <p className="text-xs text-yellow-700 mt-0.5">
                    {selectedTeacher?.name} has {makeupWarnings.length === 1 ? 'a makeup session' : 'makeup sessions'} that
                    overlap with this class schedule. You may want to reschedule {makeupWarnings.length === 1 ? 'it' : 'them'} after saving.
                  </p>
                  <ul className="mt-1.5 space-y-0.5">
                    {makeupWarnings.map(s => (
                      <li key={s.id} className="text-xs text-yellow-700 font-medium">
                        · {s.scheduledAt ? s.scheduledAt.slice(0, 16).replace('T', ' ') + ' UTC' : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmedMakeup}
                  onChange={e => setConfirmedMakeup(e.target.checked)}
                  className="rounded border-yellow-400 text-yellow-600"
                />
                <span className="text-xs text-yellow-800">I understand — save the class anyway</span>
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {(saving || checkingMakeups) && <RefreshCw size={14} className="animate-spin" />}
              {checkingMakeups ? 'Checking…' : isEditing ? 'Save Changes' : 'Create Class'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
// ─── Enroll Student Modal ──────────────────────────────────────────────────────
// Loads all users live, lets admin pick parent → child, shows already-enrolled notice.

function EnrollStudentModal({ cls, classes, teachers, allUsers, onEnroll, onClose, saving }) {
  const [search, setSearch] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null); // { user, child }
  const [sessionsTotal, setSessionsTotal] = useState(6);
  const [error, setError] = useState(null);
  const [packageMismatchConfirmed, setPackageMismatchConfirmed] = useState(false);

  // Flat list of { user, child } for picking
  const allEntries = useMemo(() => {
    const entries = [];
    allUsers.forEach(user => {
      getChildren(user).forEach(child => {
        entries.push({ user, child });
      });
    });
    return entries;
  }, [allUsers]);

  const alreadyEnrolledIds = useMemo(() => {
    const set = new Set();
    (cls.enrolledStudents ?? []).forEach(s => set.add(`${s.userId}__${s.childId}`));
    return set;
  }, [cls.enrolledStudents]);

  const filteredEntries = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return allEntries;
    return allEntries.filter(({ user, child }) =>
      child.name?.toLowerCase().includes(q) ||
      user.firstName?.toLowerCase().includes(q) ||
      user.lastName?.toLowerCase().includes(q) ||
      user.email?.toLowerCase().includes(q)
    );
  }, [allEntries, search]);

  const packageMismatch = useMemo(() => {
    if (!selectedEntry) return null;
    const compatPkgs = cls.compatiblePackages;
    if (!compatPkgs || compatPkgs.length === 0) return null;
    const childPackageId = selectedEntry.child.course?.packageId;
    if (!childPackageId || compatPkgs.includes(childPackageId)) return null;
    return {
      childPackageId,
      childPackageName: COURSE_PACKAGES[childPackageId]?.shortLabel ?? childPackageId,
      compatiblePackageNames: compatPkgs.map(id => COURSE_PACKAGES[id]?.shortLabel ?? id).join(', '),
    };
  }, [selectedEntry, cls.compatiblePackages]);

  function handleSelect(entry) {
    const key = `${entry.user.uid}__${entry.child.id}`;
    if (alreadyEnrolledIds.has(key)) return; // already enrolled, ignore
    setSelectedEntry(entry);
    setError(null);
    setPackageMismatchConfirmed(false);
  }

  function handleConfirm() {
    if (!selectedEntry) { setError('Please select a student.'); return; }
    const { user, child } = selectedEntry;

    // Hard block: check if this child is already enrolled in another class
    // that runs at the same time as the target class
    const toMins = (t) => {
      const [h, m] = t.split(':').map(Number);
      return h * 60 + m;
    };

    // Build time windows for every slot in the target class
    const targetSlots = getClassSlots(cls).map(s => ({
      dayOfWeek: s.dayOfWeek,
      start: toMins(s.time),
      end:   toMins(s.time) + Number(cls.duration ?? 60),
    }));

    // Check if any slot of the target class overlaps with any slot of any other
    // class this child is already enrolled in.
    const conflictingClasses = (classes ?? []).filter(other => {
      if (other.id === cls.id) return false;
      if (other.status === 'suspended') return false;
      const isEnrolled = (other.enrolledStudents ?? []).some(
        s => s.userId === user.uid && s.childId === child.id
      );
      if (!isEnrolled) return false;
      const otherSlots = getClassSlots(other).map(s => ({
        dayOfWeek: s.dayOfWeek,
        start: toMins(s.time),
        end:   toMins(s.time) + Number(other.duration ?? 60),
      }));
      return targetSlots.some(ts =>
        otherSlots.some(os =>
          ts.dayOfWeek === os.dayOfWeek && ts.start < os.end && os.start < ts.end
        )
      );
    });

    if (conflictingClasses.length > 0) {
      const desc = conflictingClasses.map(c => {
        const teacherName = teachers?.find(t => t.id === c.teacherId)?.name
          ?? c.teacherName
          ?? 'Unknown teacher';
        return `${formatSchedule(c)} (${teacherName})`;
      }).join(', ');
      setError(
        `${child.name} is already enrolled in a class at this time: ${desc}`
      );
      return;
    }

    // enrollmentType is always 'course' — trials are managed via TrialManager
    const student = buildEnrolledStudent(user, child, { sessionsTotal });
    onEnroll(student);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Enroll Student</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {formatSchedule(cls)} · {cls.duration} min ·{' '}
              {labelOf(DIALECTS, cls.dialect)} · {labelOf(AGE_GROUPS, cls.ageGroup)}
              {(cls.sessionsPerWeek ?? 1) > 1 && (
                <span className="ml-1.5 text-orange-600 font-medium">{cls.sessionsPerWeek}×/week</span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by child name, parent name, or email…"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              autoFocus
            />
          </div>
        </div>

        {/* Student list */}
        <div className="overflow-y-auto flex-1 p-2">
          {filteredEntries.length === 0 ? (
            <p className="text-sm text-gray-400 italic text-center py-8">No students found.</p>
          ) : (
            filteredEntries.map(({ user, child }) => {
              const key = `${user.uid}__${child.id}`;
              const enrolled = alreadyEnrolledIds.has(key);
              const isSelected = selectedEntry?.user.uid === user.uid && selectedEntry?.child.id === child.id;
              const age = calculateAge(child.dateOfBirth);

              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSelect({ user, child })}
                  disabled={enrolled}
                  className={`w-full text-left px-4 py-3 rounded-xl mb-1 transition-colors border ${
                    enrolled
                      ? 'bg-gray-50 border-gray-100 opacity-50 cursor-not-allowed'
                      : isSelected
                      ? 'bg-purple-50 border-purple-300'
                      : 'bg-white border-transparent hover:bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {child.name}
                        {age !== null && <span className="text-gray-400 font-normal text-xs ml-1">(age {age})</span>}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {user.firstName} {user.lastName} · {user.email}
                      </p>
                    </div>
                    {enrolled && (
                      <span className="text-xs text-gray-400 italic flex-shrink-0 ml-2">Enrolled</span>
                    )}
                    {isSelected && !enrolled && (
                      <CheckCircle size={16} className="text-purple-600 flex-shrink-0 ml-2" />
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Selected student config */}
        {selectedEntry && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 space-y-3">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
              Enrollment Settings for {selectedEntry.child.name}
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Sessions Total</label>
              <input
                type="number"
                min={1}
                max={100}
                value={sessionsTotal}
                onChange={e => setSessionsTotal(parseInt(e.target.value, 10))}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            {packageMismatch && (
              <div className="bg-orange-50 border border-orange-300 rounded-lg px-3 py-2.5 space-y-2">
                <p className="text-xs font-semibold text-orange-800">
                  ⚠️ Package mismatch
                </p>
                <p className="text-xs text-orange-700">
                  {selectedEntry.child.name} is enrolled in <strong>{packageMismatch.childPackageName}</strong> but this class serves <strong>{packageMismatch.compatiblePackageNames}</strong>.
                </p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={packageMismatchConfirmed}
                    onChange={e => setPackageMismatchConfirmed(e.target.checked)}
                    className="w-4 h-4 rounded border-orange-400 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-xs text-orange-800 font-medium">
                    Confirm — intentional override
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mx-4 mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedEntry || saving || (!!packageMismatch && !packageMismatchConfirmed)}
            className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving && <RefreshCw size={14} className="animate-spin" />}
            Enroll Student
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Move Student Modal ────────────────────────────────────────────────────────
// Shows compatible classes filtered by dialect, ageGroup, levelGroup.
// Carries over sessionsCompleted and sessionsTotal.

function MoveStudentModal({ student, currentClassId, compatibleClasses, teachers, onMove, onClose, saving }) {
  const [targetClassId, setTargetClassId] = useState('');
  const [error, setError] = useState(null);

  const targetClass = compatibleClasses.find(c => c.id === targetClassId);

  function handleConfirm() {
    if (!targetClassId) { setError('Please select a destination class.'); return; }
    onMove(student, targetClassId);
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Move Student</h2>
            <p className="text-sm text-gray-500 mt-0.5">{student.childName}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Progress carry-over notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
            Progress carries over: {student.sessionsCompleted} of {student.sessionsTotal} sessions completed.
            Session history is preserved.
          </div>

          {/* Compatible class picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Move to class <span className="text-red-500">*</span>
            </label>
            {compatibleClasses.length === 0 ? (
              <p className="text-sm text-gray-400 italic">
                No compatible classes found with matching dialect, age group, and level.
              </p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {compatibleClasses.map(cls => {
                  const teacher = teachers.find(t => t.id === cls.teacherId);
                  const isSelected = cls.id === targetClassId;
                  return (
                    <button
                      key={cls.id}
                      type="button"
                      onClick={() => { setTargetClassId(cls.id); setError(null); }}
                      className={`w-full text-left px-4 py-3 rounded-xl border transition-colors ${
                        isSelected
                          ? 'bg-purple-50 border-purple-300'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800 text-sm">
                            {formatSchedule(cls)}
                            {(cls.sessionsPerWeek ?? 1) > 1 && (
                              <span className="ml-1.5 text-xs text-orange-600 font-normal">{cls.sessionsPerWeek}×/week</span>
                            )}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {teacher?.name ?? cls.teacherName} · {labelOf(CLASS_TYPES, cls.type)}
                          </p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            {(cls.enrolledStudents?.length ?? 0)} student
                            {(cls.enrolledStudents?.length ?? 0) !== 1 ? 's' : ''} enrolled
                          </p>
                        </div>
                        {isSelected && <CheckCircle size={16} className="text-purple-600 flex-shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {error && (
            <p className="text-xs text-red-500">{error}</p>
          )}

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!targetClassId || saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving && <RefreshCw size={14} className="animate-spin" />}
              Confirm Move
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Roster Panel ──────────────────────────────────────────────────────────────
// Rendered inside the expanded class row. Shows enrolled students with
// parent info, progress counters, and action buttons.

function RosterPanel({ cls, classes, teachers, onUnenroll, onMove, onIncrementSessions }) {
  const students = cls.enrolledStudents ?? [];

  if (students.length === 0) {
    return (
      <p className="text-sm text-gray-400 italic py-2">
        No students enrolled. Use the Enroll button to add students.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {students.map(student => {
        // Compatible classes for the move modal:
        // same dialect + ageGroup + levelGroup, different class, active,
        // AND not conflicting with any other class the student is already enrolled in
        const toMins = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };

        // Find all other classes this student is in (excluding the source class)
        const otherEnrollments = classes.filter(c =>
          c.id !== cls.id &&
          c.status === 'active' &&
          (c.enrolledStudents ?? []).some(
            s => s.userId === student.userId && s.childId === student.childId
          )
        );

        const compatible = classes.filter(c => {
          if (c.id === cls.id) return false;
          if (c.status !== 'active') return false;
          if (c.dialect !== cls.dialect) return false;
          if (c.ageGroup !== cls.ageGroup) return false;
          if (c.levelGroup !== cls.levelGroup) return false;
          // Exclude target classes where any slot overlaps with any slot in another enrollment
          const cSlots = getClassSlots(c).map(s => ({
            dayOfWeek: s.dayOfWeek,
            start: toMins(s.time),
            end: toMins(s.time) + Number(c.duration ?? 60),
          }));
          const hasConflict = otherEnrollments.some(other => {
            const otherSlots = getClassSlots(other).map(s => ({
              dayOfWeek: s.dayOfWeek,
              start: toMins(s.time),
              end: toMins(s.time) + Number(other.duration ?? 60),
            }));
            return cSlots.some(cs =>
              otherSlots.some(os =>
                cs.dayOfWeek === os.dayOfWeek && cs.start < os.end && os.start < cs.end
              )
            );
          });
          return !hasConflict;
        });

        return (
          <div
            key={`${student.userId}__${student.childId}`}
            className="bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 flex-wrap"
          >
            {/* Student info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-gray-800 text-sm">{student.childName}</p>
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                  student.enrollmentType === 'trial'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {student.enrollmentType}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {student.parentFirstName} {student.parentLastName} · {student.parentEmail}
              </p>
              {/* Session progress */}
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 bg-gray-100 rounded-full h-1.5 max-w-[120px]">
                  <div
                    className="bg-purple-500 h-1.5 rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, ((student.sessionsCompleted ?? 0) / (student.sessionsTotal ?? 6)) * 100)}%`
                    }}
                  />
                </div>
                <span className="text-xs text-gray-500">
                  {student.sessionsCompleted ?? 0}/{student.sessionsTotal ?? 6} sessions
                </span>
                {/* Manual sessionsTotal increment — Phase 1 renewal support */}
                <button
                  onClick={() => onIncrementSessions(cls.id, student)}
                  className="text-xs text-purple-600 hover:text-purple-800 underline"
                  title="Add 6 sessions (renewal)"
                >
                  +6
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => onMove(cls, student, compatible)}
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                title="Move to another class"
              >
                <ArrowRightLeft size={15} />
              </button>
              <button
                onClick={() => onUnenroll(cls.id, student)}
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Remove from class"
              >
                <UserMinus size={15} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function ClassManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Data
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Display timezone for the class list (does not affect the form entry timezone)
  const [displayTimezone, setDisplayTimezone] = useState(
    () => localStorage.getItem('classesDisplayTimezone') ?? getBrowserTimezone()
  );
  function handleDisplayTimezoneChange(tz) {
    localStorage.setItem('classesDisplayTimezone', tz);
    setDisplayTimezone(tz);
  }

  // Filters
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [filterDay, setFilterDay] = useState('');
  const [filterType, setFilterType] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState(null);

  // Modals
  const [classFormModal, setClassFormModal] = useState(null); // null | { mode: 'add' | 'edit', cls? }
  const [enrollModal, setEnrollModal]       = useState(null); // null | { cls }
  const [moveModal, setMoveModal]           = useState(null); // null | { cls, student, compatibleClasses }
  const [saving, setSaving] = useState(false);

  // ── Data loading ────────────────────────────────────────────────────────────

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError(null);

      const [classSnap, teacherSnap, userSnap] = await Promise.all([
        getDocs(query(collection(db, 'classes'), orderBy('dayOfWeek'))),
        getDocs(query(collection(db, 'teachers'), orderBy('name'))),
        getDocs(collection(db, 'users')),
      ]);

      setClasses(classSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setTeachers(teacherSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAllUsers(userSnap.docs.map(d => ({ uid: d.id, ...d.data() })));
    } catch (err) {
      console.error('ClassManager fetch error:', err);
      setError('Failed to load data. Please refresh.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // ── Flash helper ────────────────────────────────────────────────────────────

  function showSuccess(msg) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  // ── Save class ──────────────────────────────────────────────────────────────

  const handleSaveClass = async (form) => {
    setSaving(true);
    try {
      const teacher = teachers.find(t => t.id === form.teacherId);
      // Derive slots — always from form.slots; keep flat dayOfWeek/time mirroring
      // the primary slot for backward compat with AttendanceManager.
      const slots = form.slots ?? getClassSlots(form);
      const primarySlot = slots[0] ?? { dayOfWeek: 'monday', time: '16:00' };
      const sessionsPerWeek = slots.length;

      const payload = {
        title: form.title ?? '',
        teacherId: form.teacherId,
        teacherName: teacher?.name ?? '',
        // New fields
        slots,
        sessionsPerWeek,
        // Legacy flat fields kept in sync with primary slot
        dayOfWeek: primarySlot.dayOfWeek,
        time: primarySlot.time,
        duration: form.duration ?? 60,
        type: form.type,
        dialect: form.dialect,
        ageGroup: form.ageGroup,
        levelGroup: form.levelGroup,
        notes: form.notes ?? '',
        status: form.status ?? 'active',
        compatiblePackages: form.compatiblePackages ?? [],
        // All slot times are stored in UTC. Entry-timezone conversion
        // happens in ClassFormModal before onSave is called.
        timezone: 'UTC',
      };

      if (form.id) {
        // Edit — preserve enrolledStudents
        await updateDoc(doc(db, 'classes', form.id), payload);
        showSuccess('Class updated.');
      } else {
        payload.enrolledStudents = [];
        payload.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'classes'), payload);
        showSuccess('Class created.');
      }

      setClassFormModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Save class error:', err);
      setError('Failed to save class.');
    } finally {
      setSaving(false);
    }
  };

  // ── Enroll student ──────────────────────────────────────────────────────────

  const handleEnroll = async (student) => {
    setSaving(true);
    try {
      const classId = enrollModal.cls.id;
      await updateDoc(doc(db, 'classes', classId), {
        enrolledStudents: arrayUnion(student)
      });
      showSuccess(`${student.childName} enrolled successfully.`);
      setEnrollModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Enroll error:', err);
      setError('Failed to enroll student.');
    } finally {
      setSaving(false);
    }
  };

  // ── Unenroll student ────────────────────────────────────────────────────────

  const handleUnenroll = async (classId, student) => {
    if (!window.confirm(
      `Remove ${student.childName} from this class? Session history is preserved.`
    )) return;

    try {
      await updateDoc(doc(db, 'classes', classId), {
        enrolledStudents: arrayRemove(student)
      });
      showSuccess(`${student.childName} removed from class.`);
      await fetchAll();
    } catch (err) {
      console.error('Unenroll error:', err);
      setError('Failed to remove student.');
    }
  };

  // ── Move student ────────────────────────────────────────────────────────────
  // Removes from old class, creates fresh entry on new class carrying over progress.

  const handleMove = async (student, targetClassId) => {
    setSaving(true);
    try {
      const { cls: sourceClass } = moveModal;

      // Read fresh user data to re-denormalize at move time
      const userDoc = allUsers.find(u => u.uid === student.userId);
      const childData = userDoc ? getChildren(userDoc).find(c => c.id === student.childId) : null;

      const newEntry = {
        ...(userDoc && childData
          ? buildEnrolledStudent(userDoc, childData, {
              sessionsCompleted: student.sessionsCompleted ?? 0,
              sessionsTotal: student.sessionsTotal ?? 6,
              enrollmentType: student.enrollmentType,
              enrolledAt: new Date().toISOString(),
            })
          : {
              ...student,
              enrolledAt: new Date().toISOString(),
            }
        ),
      };

      // Remove from source class
      await updateDoc(doc(db, 'classes', sourceClass.id), {
        enrolledStudents: arrayRemove(student)
      });

      // Add to target class
      await updateDoc(doc(db, 'classes', targetClassId), {
        enrolledStudents: arrayUnion(newEntry)
      });

      showSuccess(`${student.childName} moved successfully.`);
      setMoveModal(null);
      await fetchAll();
    } catch (err) {
      console.error('Move error:', err);
      setError('Failed to move student.');
    } finally {
      setSaving(false);
    }
  };

  // ── Increment sessionsTotal (+6 for renewal) ────────────────────────────────
  // Phase 1: manual. Phase 2+: Stripe webhook will call this automatically.

  const handleIncrementSessions = async (classId, student) => {
    if (!window.confirm(
      `Add 6 sessions for ${student.childName}? (${student.sessionsTotal} → ${student.sessionsTotal + 6})`
    )) return;

    try {
      const cls = classes.find(c => c.id === classId);
      if (!cls) return;

      const updatedStudents = (cls.enrolledStudents ?? []).map(s => {
        if (s.userId === student.userId && s.childId === student.childId) {
          return { ...s, sessionsTotal: (s.sessionsTotal ?? 6) + 6 };
        }
        return s;
      });

      await updateDoc(doc(db, 'classes', classId), { enrolledStudents: updatedStudents });
      showSuccess(`Sessions updated for ${student.childName}.`);
      await fetchAll();
    } catch (err) {
      console.error('Increment sessions error:', err);
      setError('Failed to update sessions.');
    }
  };

  // ── Hard delete class ───────────────────────────────────────────────────────
  // Blocked if any students are enrolled OR any session documents reference this classId.

  const handleDeleteClass = async (cls) => {
    // Guard: enrolled students
    if ((cls.enrolledStudents?.length ?? 0) > 0) {
      setError(
        `Cannot delete this class — ${cls.enrolledStudents.length} student(s) are enrolled. ` +
        `Remove all students first.`
      );
      return;
    }

    // Existing sessions are intentionally NOT a blocker.
    // Session docs — and their studentSessions/{childId} mirror — are the
    // student's permanent attendance record. Deleting the class doc does NOT
    // cascade to sessions (onClassWrite is onUpdate-only; there is no
    // onClassDelete), so the history is preserved as orphaned records. We
    // surface the count in the confirmation so the admin knows what is kept.
    //
    // The count query is wrapped so that if it fails (index/permissions/
    // network) we show an error and cancel, rather than throwing an
    // unhandled rejection that makes the button look dead.
    let sessionCount = 0;
    try {
      const sessionSnap = await getDocs(
        query(collection(db, 'sessions'), where('classId', '==', cls.id))
      );
      sessionCount = sessionSnap.size;
    } catch (err) {
      console.error('Delete class — session count lookup failed:', err);
      setError(
        'Could not check this class for existing sessions, so deletion was ' +
        'cancelled to be safe. Please try again — see the browser console for details.'
      );
      return;
    }

    const sessionNote = sessionCount > 0
      ? `\n\n${sessionCount} session record(s) exist for this class. They will ` +
        `be PRESERVED as attendance history — each student's Session History ` +
        `keeps the date, teacher, and attendance for every session. Only the ` +
        `class will be removed.\n\nDo NOT delete the sessions from the ` +
        `Attendance screen if you want to keep that history — that would erase it.`
      : '';

    if (!window.confirm(
      `Permanently delete this class (${cls.dayOfWeek} at ${cls.time} with ${cls.teacherName})?` +
      sessionNote +
      `\n\nThis cannot be undone.`
    )) return;

    try {
      await deleteDoc(doc(db, 'classes', cls.id));
      showSuccess('Class deleted.');
      if (expandedId === cls.id) setExpandedId(null);
      await fetchAll();
    } catch (err) {
      console.error('Delete class error:', err);
      setError('Failed to delete class.');
    }
  };

  // ── Filtered classes ────────────────────────────────────────────────────────

  const filteredClasses = useMemo(() => {
    return classes.filter(cls => {
      if (filterStatus && cls.status !== filterStatus) return false;
      if (filterTeacher && cls.teacherId !== filterTeacher) return false;
      if (filterDay && !getClassSlots(cls).some(s => s.dayOfWeek === filterDay)) return false;
      if (filterType && cls.type !== filterType) return false;
      return true;
    });
  }, [classes, filterStatus, filterTeacher, filterDay, filterType]);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading classes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Classes</h1>
              <p className="text-sm text-gray-500">
                {classes.filter(c => c.status === 'active').length} active ·{' '}
                {classes.reduce((n, c) => n + (c.enrolledStudents?.length ?? 0), 0)} total enrollments
              </p>
            </div>
          </div>
          <select
            value={displayTimezone}
            onChange={e => handleDisplayTimezoneChange(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            title="Display timezone for class times"
          >
            {COMMON_TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          <button
            onClick={() => setClassFormModal({ mode: 'add' })}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus size={16} />
            Create Class
          </button>
        </div>

        {/* Flash messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <CheckCircle size={16} /> {successMessage}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} /> {error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {/* Status */}
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>

          {/* Teacher */}
          <select
            value={filterTeacher}
            onChange={e => setFilterTeacher(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All teachers</option>
            {teachers.filter(t => t.active).map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>

          {/* Day */}
          <select
            value={filterDay}
            onChange={e => setFilterDay(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All days</option>
            {DAYS_OF_WEEK.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>

          {/* Type */}
          <select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">All types</option>
            {CLASS_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>

          {/* Reset */}
          {(filterStatus || filterTeacher || filterDay || filterType) && (
            <button
              onClick={() => { setFilterStatus('active'); setFilterTeacher(''); setFilterDay(''); setFilterType(''); }}
              className="text-xs text-gray-400 hover:text-gray-600 px-2"
            >
              Reset filters
            </button>
          )}
        </div>

        {/* Class list */}
        {filteredClasses.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <BookOpen size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No classes found</p>
            <p className="text-sm text-gray-400 mt-1">
              {classes.length === 0
                ? 'Create your first class to get started.'
                : 'Try adjusting the filters above.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredClasses.map(cls => {
              const teacher = teachers.find(t => t.id === cls.teacherId);
              const isExpanded = expandedId === cls.id;
              const studentCount = cls.enrolledStudents?.length ?? 0;

              return (
                <div
                  key={cls.id}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden"
                >
                  {/* Class header row */}
                  <div
                    className="flex items-center gap-4 px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors flex-wrap"
                    onClick={() => setExpandedId(isExpanded ? null : cls.id)}
                  >
                    {/* Teacher color dot */}
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: teacher?.color ?? '#7C3AED' }}
                    />

                    {/* Schedule — all slots, each with a timezone tooltip on hover */}
                    <div className="min-w-[150px]">
                      {cls.title && (
                        <p className="font-bold text-gray-900 text-sm leading-snug">{cls.title}</p>
                      )}
                      <p className="font-semibold text-gray-800 text-sm flex flex-wrap items-center gap-x-1">
                        {getClassSlots(cls).map((s, i) => {
                          const { localDay } = timeFromUTC(s.time, s.dayOfWeek, displayTimezone);
                          const displayDayLabel = DAYS_OF_WEEK.find(d => d.value === localDay)?.label ?? localDay;
                          return (
                            <React.Fragment key={i}>
                              {i > 0 && <span className="text-gray-400">·</span>}
                              <TimezoneTooltip timeStr={s.time} dayOfWeek={s.dayOfWeek}>
                                <span className="cursor-help underline decoration-dotted decoration-gray-400 underline-offset-2">
                                  {displayDayLabel.slice(0, 3)} {formatTimeDisplay(s.time, displayTimezone)}
                                </span>
                              </TimezoneTooltip>
                            </React.Fragment>
                          );
                        })}
                      </p>
                      <p className="text-xs text-gray-400">
                        {cls.duration} min · {COMMON_TIMEZONES.find(tz => tz.value === displayTimezone)?.label ?? displayTimezone}
                      </p>
                    </div>

                    {/* Teacher */}
                    <div className="min-w-[120px]">
                      <p className="text-sm text-gray-700">{teacher?.name ?? cls.teacherName}</p>
                      <p className="text-xs text-gray-400">{labelOf(DIALECTS, cls.dialect)}</p>
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <TypeBadge type={cls.type} />
                      <IntensityBadge sessionsPerWeek={cls.sessionsPerWeek} />
                      <StatusBadge status={cls.status} />
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Users size={12} />
                        {studentCount} student{studentCount !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Age + level */}
                    <div className="ml-auto text-xs text-gray-400 hidden md:block">
                      {labelOf(AGE_GROUPS, cls.ageGroup)} · {labelOf(LEVEL_GROUPS, cls.levelGroup)}
                    </div>

                    {/* Actions */}
                    <div
                      className="flex items-center gap-1 flex-shrink-0"
                      onClick={e => e.stopPropagation()}
                    >
                      <button
                        onClick={() => setEnrollModal({ cls })}
                        className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                        title="Enroll student"
                      >
                        <UserPlus size={15} />
                      </button>
                      <button
                        onClick={() => setClassFormModal({ mode: 'edit', cls })}
                        className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                        title="Edit class"
                      >
                        <Edit2 size={15} />
                      </button>
                      <button
                        onClick={() => handleDeleteClass(cls)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete class"
                      >
                        <Trash2 size={15} />
                      </button>
                      <span className="text-gray-300 ml-1">
                        {isExpanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                      </span>
                    </div>
                  </div>

                  {/* Expanded roster */}
                  {isExpanded && (
                    <>
                      <div className="px-5 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-3 flex-wrap">
                        <CopyableId label="Class ID" value={cls.id} />
                      </div>
                      <div className="border-t border-gray-100 px-5 py-4 bg-gray-50">
                        {cls.notes && (
                          <p className="text-xs text-gray-500 italic mb-3">📝 {cls.notes}</p>
                        )}
                        <div className="flex items-center justify-between mb-3">
                          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Enrolled Students
                          </p>
                          <button
                            onClick={() => setEnrollModal({ cls })}
                            className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
                          >
                            <Plus size={12} /> Enroll student
                          </button>
                        </div>
                        <RosterPanel
                          cls={cls}
                          classes={classes}
                          teachers={teachers}
                          onUnenroll={handleUnenroll}
                          onMove={(cls, student, compatible) =>
                            setMoveModal({ cls, student, compatibleClasses: compatible })
                          }
                          onIncrementSessions={handleIncrementSessions}
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Class form modal */}
      {classFormModal && (
        <ClassFormModal
          cls={classFormModal.mode === 'edit' ? classFormModal.cls : null}
          teachers={teachers}
          classes={classes}
          onSave={handleSaveClass}
          onClose={() => setClassFormModal(null)}
          saving={saving}
        />
      )}

      {/* Enroll modal */}
      {enrollModal && (
        <EnrollStudentModal
          cls={enrollModal.cls}
          classes={classes}
          teachers={teachers}
          allUsers={allUsers}
          onEnroll={handleEnroll}
          onClose={() => setEnrollModal(null)}
          saving={saving}
        />
      )}

      {/* Move modal */}
      {moveModal && (
        <MoveStudentModal
          student={moveModal.student}
          currentClassId={moveModal.cls.id}
          compatibleClasses={moveModal.compatibleClasses}
          teachers={teachers}
          onMove={handleMove}
          onClose={() => setMoveModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

export default ClassManager;